import { defaultCollectionAuth, defaultRequestAuth } from '../requestAuth';

const HTTP_METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS']);

export function joinFolderName(prefix, name) {
  const n = String(name || '').trim();
  if (!n) return prefix || '';
  if (!prefix) return n;
  return `${prefix} / ${n}`;
}

export function toKvRows(items, { disabledKey = 'disabled' } = {}) {
  if (!Array.isArray(items)) return [];
  return items
    .filter((row) => row && (row.key != null || row.name != null))
    .map((row) => {
      const key = String(row.key ?? row.name ?? '').trim();
      const value = String(row.value ?? row.val ?? '').trim();
      const disabled = row[disabledKey] === true || row.enabled === false;
      return { key, value, enabled: !disabled };
    })
    .filter((row) => row.key || row.value);
}

export function serializeFormBody(fields) {
  const params = new URLSearchParams();
  for (const row of fields) {
    if (!row.key) continue;
    params.append(row.key, row.value ?? '');
  }
  return params.toString();
}

export function mapPostmanAuth(auth, warnings, context = 'request') {
  if (!auth || auth.type === 'noauth') {
    return context === 'collection' ? defaultCollectionAuth() : defaultRequestAuth();
  }

  const type = String(auth.type || '').toLowerCase();

  switch (type) {
    case 'bearer': {
      const token = auth.bearer?.[0]?.value ?? auth.bearer ?? '';
      return {
        ...(context === 'collection' ? defaultCollectionAuth() : defaultRequestAuth()),
        type: 'bearer',
        bearer: Array.isArray(token) ? token[0]?.value ?? '' : String(token),
      };
    }
    case 'basic': {
      const user = auth.basic?.[0]?.value ?? auth.basic?.username ?? '';
      const pass = auth.basic?.[1]?.value ?? auth.basic?.password ?? '';
      return {
        ...(context === 'collection' ? defaultCollectionAuth() : defaultRequestAuth()),
        type: 'basic',
        basic: {
          username: Array.isArray(auth.basic)
            ? auth.basic.find((x) => x.key === 'username')?.value ?? user
            : String(user),
          password: Array.isArray(auth.basic)
            ? auth.basic.find((x) => x.key === 'password')?.value ?? pass
            : String(pass),
        },
      };
    }
    case 'apikey': {
      const entries = Array.isArray(auth.apikey) ? auth.apikey : [];
      const keyEntry = entries.find((x) => x.key === 'key') || entries[0];
      const valEntry = entries.find((x) => x.key === 'value') || entries[1];
      const inEntry = entries.find((x) => x.key === 'in') || entries[2];
      return {
        ...(context === 'collection' ? defaultCollectionAuth() : defaultRequestAuth()),
        type: 'apikey',
        apikey: {
          key: keyEntry?.value ?? '',
          value: valEntry?.value ?? '',
          in: inEntry?.value === 'query' ? 'query' : 'header',
        },
      };
    }
    case 'oauth2': {
      const entries = Array.isArray(auth.oauth2) ? auth.oauth2 : [];
      const read = (key) => entries.find((x) => x.key === key)?.value ?? '';
      const grantRaw = read('grant_type');
      let grantType = 'manual';
      if (read('accessToken')) {
        grantType = 'manual';
      } else if (grantRaw.includes('pkce') || grantRaw === 'authorization_code_with_pkce') {
        grantType = 'authorization_code_pkce';
      } else if (grantRaw === 'client_credentials') {
        grantType = 'client_credentials';
      } else if (grantRaw && grantRaw !== 'implicit' && read('authUrl') && read('accessTokenUrl')) {
        grantType = 'authorization_code_pkce';
      }
      return {
        ...(context === 'collection' ? defaultCollectionAuth() : defaultRequestAuth()),
        type: 'oauth2',
        oauth2: {
          grantType,
          authUrl: read('authUrl'),
          tokenUrl: read('accessTokenUrl') || read('tokenUrl'),
          clientId: read('clientId'),
          clientSecret: read('clientSecret'),
          scope: read('scope'),
          redirectUri: read('redirect_uri') || read('redirectUri'),
          accessToken: read('accessToken'),
          refreshToken: read('refreshToken'),
          tokenType: read('tokenType') || 'Bearer',
          expiresAt: null,
        },
      };
    }
    case 'inherit':
      return defaultRequestAuth();
    default:
      warnings.push(`Unsupported ${context} auth type: ${type || 'unknown'}`);
      return context === 'collection' ? defaultCollectionAuth() : defaultRequestAuth();
  }
}

export function mapInsomniaAuth(authentication, warnings) {
  if (!authentication || authentication.disabled) {
    return defaultRequestAuth();
  }
  const type = String(authentication.type || '').toLowerCase();
  if (type === 'bearer') {
    return { ...defaultRequestAuth(), type: 'bearer', bearer: authentication.token ?? '' };
  }
  if (type === 'basic') {
    return {
      ...defaultRequestAuth(),
      type: 'basic',
      basic: {
        username: authentication.username ?? '',
        password: authentication.password ?? '',
      },
    }
  }
  warnings.push(`Unsupported Insomnia auth type: ${type || 'unknown'}`);
  return defaultRequestAuth();
}

export function mapPostmanBody(body, warnings) {
  if (!body || !body.mode) {
    return { body: '', body_type: 'json' };
  }

  const mode = String(body.mode).toLowerCase();

  if (mode === 'raw') {
    const raw = body.raw ?? '';
    const opts = body.options?.raw || {};
    const lang = String(opts.language || '').toLowerCase();
    if (lang === 'json' || (raw.trim().startsWith('{') || raw.trim().startsWith('['))) {
      return { body: raw, body_type: 'json' };
    }
    return { body: raw, body_type: 'raw' };
  }

  if (mode === 'urlencoded') {
    const form_data_fields = (body.urlencoded || [])
      .filter((f) => f && (f.key != null || f.name != null))
      .map((f) => ({
        key: String(f.key ?? f.name ?? ''),
        value: String(f.value ?? ''),
        type: 'text',
        enabled: f.disabled !== true,
        filePath: null,
      }))
      .filter((row) => row.key);
    const enabledRows = form_data_fields.filter((row) => row.enabled !== false);
    return {
      body: serializeFormBody(enabledRows),
      body_type: 'form',
      form_data_fields,
    };
  }

  if (mode === 'formdata') {
    const form_data_fields = (body.formdata || [])
      .filter((f) => f && (f.key != null || f.name != null))
      .map((f) => {
        const isFile = f.type === 'file';
        if (isFile) {
          // COL-01. Postman's `src` is a path on the machine that EXPORTED the
          // collection, and the collection is attacker-controlled — a shared
          // file naming `/Users/victim/.ssh/id_rsa` used to be copied straight
          // into `filePath` and read off disk by the Rust sender on Send (or
          // once per request on "Run collection"). An imported path is a hint
          // about what the author uploaded, never a capability to read that
          // file here, so it lands in `filePathHint` (display only) and
          // `filePath` stays null until the user picks a file themselves.
          // The warning is now unconditional for the same reason: with or
          // without `src`, this row needs a real pick before it can send.
          warnings.push(
            `Form-data file field "${f.key || ''}" needs a local file; pick one after import`
          );
        }
        const hint = isFile ? String(f.src ?? '') : '';
        return {
          key: String(f.key ?? f.name ?? ''),
          value: isFile ? '' : String(f.value ?? ''),
          type: isFile ? 'file' : 'text',
          enabled: f.disabled !== true,
          filePath: null,
          ...(hint ? { filePathHint: hint } : {}),
        };
      })
      .filter((row) => row.key);
    return {
      body: '',
      body_type: 'formdata',
      form_data_fields,
    };
  }

  if (mode === 'graphql') {
    const query = body.graphql?.query ?? '';
    const variablesRaw = body.graphql?.variables ?? '';
    const payload = { query };
    if (variablesRaw !== '' && variablesRaw != null) {
      payload.variables = tryParseJson(variablesRaw);
    }
    if (body.graphql?.operationName) payload.operationName = body.graphql.operationName;
    return { body: JSON.stringify(payload), body_type: 'graphql' };
  }

  if (mode === 'file') {
    warnings.push('Binary file body was not imported');
    return { body: '', body_type: 'binary' };
  }

  return { body: body.raw ?? '', body_type: 'raw' };
}

const PREVIEW_LANG_CONTENT_TYPE = {
  json: 'application/json',
  xml: 'application/xml',
  html: 'text/html',
  text: 'text/plain',
  javascript: 'application/javascript',
};

/**
 * Map Postman saved examples (`item.response[]`) to our mock `responseExamples`.
 * Each Postman response carries a `code`, header list, and body; we derive the
 * content-type from an explicit Content-Type header or the preview language.
 */
export function mapPostmanResponses(responses) {
  if (!Array.isArray(responses) || responses.length === 0) return null;
  const responseExamples = responses
    .filter((r) => r && typeof r === 'object')
    .map((r, idx) => {
      const headers = toKvRows(r.header || []);
      const ctHeader = headers.find((h) => h.key.toLowerCase() === 'content-type');
      const previewLang = String(r._postman_previewlanguage || '').toLowerCase();
      const contentType =
        (ctHeader?.value || '').split(';')[0].trim() ||
        PREVIEW_LANG_CONTENT_TYPE[previewLang] ||
        'application/json';
      return {
        id: r.id || `ex-import-${idx}`,
        name: r.name || '',
        status: Number(r.code) || 200,
        contentType,
        headers,
        body: typeof r.body === 'string' ? r.body : '',
        matchQuery: [],
        matchHeaders: [],
        enabled: true,
        sourceType: 'example',
        schemaId: null,
      };
    });
  return { responseExamples };
}

function tryParseJson(str) {
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
}

export function mapInsomniaBody(body, warnings) {
  if (!body) return { body: '', body_type: 'json' };
  const mime = String(body.mimeType || '').toLowerCase();
  const text = body.text ?? '';

  if (mime.includes('json')) {
    return { body: text, body_type: 'json' };
  }
  if (mime.includes('x-www-form-urlencoded')) {
    return { body: text, body_type: 'form' };
  }
  if (mime.includes('multipart')) {
    warnings.push('Multipart body imported as raw');
    return { body: text, body_type: 'raw' };
  }
  return { body: text, body_type: 'raw' };
}

export function extractPostmanUrl(urlField) {
  if (!urlField) return { url: '', params: [] };
  if (typeof urlField === 'string') return { url: urlField, params: [] };

  const raw = urlField.raw;
  if (raw) {
    const queryRows = toKvRows(urlField.query || []);
    return { url: raw, params: queryRows };
  }

  const protocol = urlField.protocol || 'https';
  const host = Array.isArray(urlField.host) ? urlField.host.join('.') : urlField.host || '';
  const port = urlField.port ? `:${urlField.port}` : '';
  const pathPart = Array.isArray(urlField.path) ? `/${urlField.path.join('/')}` : urlField.path || '';
  const base = host ? `${protocol}://${host}${port}${pathPart}` : '';
  const queryRows = toKvRows(urlField.query || []);
  return { url: base, params: queryRows };
}

export function normalizeMethod(method) {
  const m = String(method || 'GET').toUpperCase();
  return HTTP_METHODS.has(m) ? m : 'GET';
}

export function variablesToCollectionSettings(variables) {
  const rows = toKvRows(
    (variables || []).map((v) => ({
      key: v.key ?? v.id,
      value: v.value ?? '',
      disabled: v.disabled === true,
    }))
  );
  return rows.length ? { collection_variables: rows } : {};
}

export function schemaExampleFromJsonSchema(schema) {
  if (!schema || typeof schema !== 'object') return '{}';
  if (schema.example != null) {
    return typeof schema.example === 'string'
      ? schema.example
      : JSON.stringify(schema.example, null, 2);
  }
  if (schema.default != null) {
    return typeof schema.default === 'string'
      ? schema.default
      : JSON.stringify(schema.default, null, 2);
  }
  const type = schema.type || 'object';
  if (type === 'object' && schema.properties) {
    const obj = {};
    for (const [key, prop] of Object.entries(schema.properties)) {
      obj[key] = exampleValueForSchema(prop);
    }
    return JSON.stringify(obj, null, 2);
  }
  if (type === 'array' && schema.items) {
    return JSON.stringify([exampleValueForSchema(schema.items)], null, 2);
  }
  return JSON.stringify(exampleValueForSchema(schema), null, 2);
}

function exampleValueForSchema(schema) {
  if (!schema || typeof schema !== 'object') return null;
  if (schema.example != null) return schema.example;
  if (schema.default != null) return schema.default;
  switch (schema.type) {
    case 'string':
      return schema.enum?.[0] ?? 'string';
    case 'integer':
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return schema.items ? [exampleValueForSchema(schema.items)] : [];
    case 'object':
      if (schema.properties) {
        const o = {};
        for (const [k, v] of Object.entries(schema.properties)) {
          o[k] = exampleValueForSchema(v);
        }
        return o;
      }
      return {};
    default:
      return null;
  }
}
